import React, { useMemo, useState } from 'react';
import { useAdminRecipes } from '../../hooks/useAdminRecipes.js';
import { useLucideIcons } from '../../hooks/usePageAssets.js';

const normalizeProcessName = (value = '') =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

const isDonutPreparationProcess = (processName) => {
  if (!processName) {
    return false;
  }
  return normalizeProcessName(processName).includes('preparacion de dona');
};

const RecipesSection = ({ onNotify }) => {
  const {
    items,
    processes,
    articles,
    warmers,
    optionsLoading,
    optionsError,
    processFilter,
    setProcessFilter,
    loading,
    error,
    refresh,
    refreshOptions,
    saveRecipe,
    deleteRecipe,
    uploadImage
  } = useAdminRecipes();

  const [search, setSearch] = useState('');
  const [editorState, setEditorState] = useState({ open: false, record: null, saving: false });

  useLucideIcons([items.length, search, processFilter, editorState.open]);

  const filteredItems = useMemo(() => {
    const query = search.toLowerCase();
    return items.filter((recipe) => {
      if (!query) {
        return true;
      }
      const name = recipe.nombre?.toLowerCase() || '';
      const article = recipe.articulo_nombre?.toLowerCase() || '';
      const process = recipe.proceso_nombre?.toLowerCase() || '';
      const plus = [recipe.plu1, recipe.plu2, recipe.plu3, recipe.plu4, recipe.plu5, recipe.plu6]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return name.includes(query) || article.includes(query) || process.includes(query) || plus.includes(query);
    });
  }, [items, search]);

  const handleOpenModal = (record = null) => {
    if (!optionsLoading && (articles.length === 0 || processes.length === 0 || optionsError)) {
      refreshOptions();
    }
    setEditorState({
      open: true,
      saving: false,
      record: record
        ? { ...record }
        : {
          plu1: '',
          plu2: '',
          plu3: '',
          plu4: '',
          plu5: '',
          plu6: '',
          nombre: '',
          articulo_id: '',
          proceso_id: '',
          activo: true,
          imagen_url: ''
        }
    });
  };

  const handleSave = async (values) => {
    setEditorState((prev) => ({ ...prev, saving: true }));
    try {
      await saveRecipe(values);
      setEditorState({ open: false, record: null, saving: false });
      refresh();
      onNotify?.({
        type: 'success',
        message: values?.id ? 'Receta actualizada correctamente.' : 'Receta creada correctamente.'
      });
    } catch (err) {
      setEditorState((prev) => ({ ...prev, saving: false }));
      onNotify?.({ type: 'error', message: err?.message || 'No se pudo guardar la receta.' });
    }
  };

  const handleDelete = async (record) => {
    const confirmed = window.confirm(`¿Eliminar la receta "${record.nombre}"?`);
    if (!confirmed) {
      return;
    }
    try {
      await deleteRecipe(record.id);
      refresh();
      onNotify?.({ type: 'success', message: 'Receta eliminada.' });
    } catch (err) {
      onNotify?.({ type: 'error', message: err?.message || 'No se pudo eliminar la receta.' });
    }
  };

  const closeModal = () => {
    setEditorState({ open: false, record: null, saving: false });
  };

  return (
    <section id="recipes-section" className="admin-section active">
      <div className="section-header">
        <h2 className="section-title">Recetas</h2>
        <div className="section-actions">
          <div className="search-container">
            <span className="search-icon"><i data-lucide="search" /></span>
            <input
              type="text"
              className="form-input"
              placeholder="Buscar por nombre, artículo, proceso o PLU..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            {search ? (
              <button type="button" className="btn-secondary" onClick={() => setSearch('')}>
                Limpiar
              </button>
            ) : null}
          </div>
          <div className="button-group">
            <div className="form-group">
              <select
                className="form-select"
                value={processFilter}
                onChange={(event) => setProcessFilter(event.target.value)}
              >
                <option value="">Todos los procesos</option>
                {processes.map((process) => (
                  <option key={process.id} value={process.id}>
                    {process.nombre}
                  </option>
                ))}
              </select>
            </div>
            <button className="btn-primary" type="button" onClick={() => handleOpenModal()}>
              <span className="btn-icon"><i data-lucide="plus" /></span>
              Nueva Receta
            </button>
          </div>
          {optionsLoading ? (
            <div className="empty-state-subtext">Actualizando artículos, procesos y warmers...</div>
          ) : null}
          {optionsError ? (
            <div className="empty-state-subtext" role="status">
              {optionsError}{' '}
              <button className="btn-secondary" type="button" onClick={refreshOptions}>Reintentar</button>
            </div>
          ) : null}
        </div>
      </div>
      <div className="data-table-container">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner" />
            <div className="loading-text">Cargando recetas...</div>
          </div>
        ) : (
          <table className="data-table" id="recipes-table">
            <thead>
              <tr>
                <th>Imagen</th>
                <th>PLUs</th>
                <th>Nombre</th>
                <th>Artículo vinculado</th>
                <th>Proceso</th>
                <th>Warmers</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {error ? (
                <tr>
                  <td colSpan={8} className="empty-state">
                    <div className="empty-state-icon"><i data-lucide="alert-triangle" /></div>
                    <div className="empty-state-text">{error}</div>
                  </td>
                </tr>
              ) : filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={8} className="empty-state">
                    <div className="empty-state-icon"><i data-lucide="chef-hat" /></div>
                    <div className="empty-state-text">No hay recetas configuradas.</div>
                    <div className="empty-state-subtext">Crea tu primera receta para comenzar.</div>
                  </td>
                </tr>
              ) : (
                filteredItems.map((recipe) => (
                  <tr key={recipe.id}>
                    <td>
                      {recipe.imagen_url ? (
                        <img src={recipe.imagen_url} alt={recipe.nombre} className="table-image" />
                      ) : (
                        <div className="table-image-placeholder"><i data-lucide="image-off" /></div>
                      )}
                    </td>
                    <td className="plu-list">
                      {[recipe.plu1, recipe.plu2, recipe.plu3, recipe.plu4, recipe.plu5, recipe.plu6]
                        .filter(Boolean)
                        .map((plu) => (
                          <span className="plu-tag" key={`${recipe.id}-${plu}`}>{plu}</span>
                        ))}
                    </td>
                    <td>{recipe.nombre}</td>
                    <td>{recipe.articulo_nombre || 'Sin artículo vinculado'}</td>
                    <td>{recipe.proceso_nombre || 'Sin proceso'}</td>
                    <td>
                      {recipe.warmers?.length ? (
                        <span
                          className="status-badge status-neutral"
                          title={recipe.warmers.map((item) => `${item.warmer_nombre || 'Warmer'}: ${item.minutos} min`).join(' · ')}
                        >
                          {recipe.warmers.length} configurado{recipe.warmers.length === 1 ? '' : 's'}
                        </span>
                      ) : (
                        <span className="muted-text">{recipe.proceso_es_warmer ? 'Sin tiempos' : '—'}</span>
                      )}
                    </td>
                    <td>
                      <span className={`status-badge ${recipe.activo ? 'status-active' : 'status-inactive'}`}>
                        {recipe.activo ? 'Activa' : 'Inactiva'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button className="action-btn edit" type="button" onClick={() => handleOpenModal(recipe)}>
                          <i data-lucide="edit" />
                        </button>
                        <button className="action-btn delete" type="button" onClick={() => handleDelete(recipe)}>
                          <i data-lucide="trash-2" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
      {editorState.open ? (
        <RecipeModal
          state={editorState}
        processes={processes}
        articles={articles}
        warmers={warmers}
        optionsLoading={optionsLoading}
        optionsError={optionsError}
        onReloadOptions={refreshOptions}
          onClose={closeModal}
          onSubmit={handleSave}
          onUpload={uploadImage}
          onNotify={onNotify}
        />
      ) : null}
    </section>
  );
};

const RecipeModal = ({
  state,
  processes,
  articles,
  warmers,
  optionsLoading,
  optionsError,
  onReloadOptions,
  onClose,
  onSubmit,
  onUpload,
  onNotify
}) => {
  console.log('RecipeModal rendered with articles:', articles?.length, articles);
  console.log('RecipeModal rendered with processes:', processes?.length, processes);
  
  const [formValues, setFormValues] = useState(() => {
    const hasCustomLifespan = state.record?.tiempo_vida_minutos !== null
      && typeof state.record?.tiempo_vida_minutos !== 'undefined';
    return {
      ...state.record,
      plu1: state.record?.plu1 || '',
      plu2: state.record?.plu2 || '',
      plu3: state.record?.plu3 || '',
      plu4: state.record?.plu4 || '',
      plu5: state.record?.plu5 || '',
      plu6: state.record?.plu6 || '',
      nombre: state.record?.nombre || '',
      articulo_id: state.record?.articulo_id || '',
      proceso_id: state.record?.proceso_id || '',
      warmer_minutes: state.record?.warmer_minutes
        || Object.fromEntries((state.record?.warmers || []).map((item) => [item.warmer_id, item.minutos])),
      tiempo_vida_minutos: hasCustomLifespan ? state.record?.tiempo_vida_minutos : '',
      lifespan_mode: hasCustomLifespan ? 'custom' : 'linked',
      activo: state.record?.activo ?? true,
      imagen_url: state.record?.imagen_url || ''
    };
  });

  const [imageState, setImageState] = useState({ uploading: false });
  const selectedProcess = useMemo(
    () => processes.find((process) => String(process.id) === String(formValues.proceso_id)),
    [processes, formValues.proceso_id]
  );
  const linkedArticle = useMemo(
    () => articles.find((article) => String(article.id) === String(formValues.articulo_id)),
    [articles, formValues.articulo_id]
  );
  const linkedMinutes = linkedArticle?.tiempo_vida_minutos;
  const linkedMinutesLabel = Number.isFinite(linkedMinutes) ? ` (${linkedMinutes} min)` : '';
  const isWarmerProcess = selectedProcess
    ? Boolean(selectedProcess.es_warmer)
    : Boolean(state.record?.proceso_es_warmer);
  const currentProcessName = formValues.proceso_id ? selectedProcess?.nombre : state.record?.proceso_nombre;
  const isDonutProcess = useMemo(
    () => isDonutPreparationProcess(currentProcessName),
    [currentProcessName]
  );

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormValues((prev) => {
      const next = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };
      if (name === 'proceso_id') {
        const targetProcess = processes.find((process) => String(process.id) === String(value));
        if (!targetProcess?.es_warmer) {
          next.warmer_minutes = {};
        }
      }
      return next;
    });
  };

  const handleWarmerMinutesChange = (warmerId, minutes) => {
    setFormValues((prev) => ({
      ...prev,
      warmer_minutes: {
        ...(prev.warmer_minutes || {}),
        [warmerId]: minutes
      }
    }));
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    setImageState({ uploading: true });
    try {
      const data = await onUpload({ file, entityId: formValues.id });
      setFormValues((prev) => ({ ...prev, imagen_url: data.image_url }));
      onNotify?.({ type: 'success', message: data.message || 'Imagen subida correctamente.' });
    } catch (err) {
      onNotify?.({ type: 'error', message: err?.message || 'No se pudo subir la imagen.' });
    } finally {
      setImageState({ uploading: false });
      event.target.value = '';
    }
  };

  const handleRemoveImage = () => {
    setFormValues((prev) => ({ ...prev, imagen_url: '' }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const payload = { ...formValues, id: state.record?.id || null };
    delete payload.lifespan_mode;
    if (isDonutProcess) {
      if (formValues.lifespan_mode === 'custom') {
        const minutes = Number(formValues.tiempo_vida_minutos);
        if (!Number.isFinite(minutes) || minutes < 0) {
          onNotify?.({ type: 'error', message: 'Ingresa un tiempo de vida válido en minutos.' });
          return;
        }
        payload.tiempo_vida_minutos = Math.max(0, Math.round(minutes));
      } else {
        payload.tiempo_vida_minutos = null;
      }
    } else {
      payload.tiempo_vida_minutos = null;
    }
    onSubmit(payload);
  };

  return (
    <div className="modal" style={{ display: 'block' }}>
      <div className="modal-content" style={{ maxWidth: '720px' }}>
        <div className="modal-header">
          <h3>{state.record?.id ? 'Editar Receta' : 'Nueva Receta'}</h3>
          <button className="modal-close" type="button" onClick={onClose}>
            <i data-lucide="x" />
          </button>
        </div>
        <form className="modal-body form-grid" onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">PLU 1</label>
            <input
              type="text"
              className="form-input"
              name="plu1"
              value={formValues.plu1}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">PLU 2</label>
            <input
              type="text"
              className="form-input"
              name="plu2"
              value={formValues.plu2}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label className="form-label">PLU 3</label>
            <input
              type="text"
              className="form-input"
              name="plu3"
              value={formValues.plu3}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label className="form-label">PLU 4</label>
            <input
              type="text"
              className="form-input"
              name="plu4"
              value={formValues.plu4}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label className="form-label">PLU 5</label>
            <input
              type="text"
              className="form-input"
              name="plu5"
              value={formValues.plu5}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label className="form-label">PLU 6</label>
            <input
              type="text"
              className="form-input"
              name="plu6"
              value={formValues.plu6}
              onChange={handleChange}
            />
          </div>
          <div className="form-group full-width">
            <label className="form-label">Nombre</label>
            <input
              type="text"
              className="form-input"
              name="nombre"
              value={formValues.nombre}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">Artículo vinculado *</label>
            <select
              className="form-select"
              name="articulo_id"
              value={formValues.articulo_id}
              onChange={handleChange}
              required
              disabled={optionsLoading || articles.length === 0}
            >
              <option value="">Seleccionar artículo</option>
              {articles.map((article) => (
                <option key={article.id} value={article.id}>
                  {article.nombre}
                </option>
              ))}
            </select>
            {optionsLoading ? (
              <div className="empty-state-subtext">Cargando artículos, procesos y warmers...</div>
            ) : null}
            {optionsError ? (
              <div className="empty-state-subtext">
                {optionsError}{' '}
                <button className="btn-secondary" type="button" onClick={onReloadOptions}>Reintentar</button>
              </div>
            ) : null}
            {!optionsLoading && !optionsError && articles.length === 0 ? (
              <div className="empty-state-subtext">No hay artículos disponibles. Revisa la sección de artículos o intenta recargar.</div>
            ) : null}
          </div>
          <div className="form-group">
            <label className="form-label">Proceso</label>
            <select
              className="form-select"
              name="proceso_id"
              value={formValues.proceso_id}
              onChange={handleChange}
              disabled={optionsLoading || processes.length === 0}
            >
              <option value="">Seleccionar proceso</option>
              {processes.map((process) => (
                <option key={process.id} value={process.id}>
                  {process.nombre}
                </option>
              ))}
            </select>
          </div>
          {isDonutProcess ? (
            <>
              <div className="form-group full-width">
                <label className="form-label">Tiempo de vida</label>
                <select
                  className="form-select"
                  name="lifespan_mode"
                  value={formValues.lifespan_mode}
                  onChange={handleChange}
                >
                  <option value="linked">Usar tiempo de vida del artículo vinculado</option>
                  <option value="custom">Tiempo de vida personalizado</option>
                </select>
                {formValues.lifespan_mode === 'linked' ? (
                  <div className="empty-state-subtext">
                    Se usa el tiempo de vida del artículo vinculado
                    {linkedMinutesLabel}.
                  </div>
                ) : null}
              </div>
              {formValues.lifespan_mode === 'custom' ? (
                <div className="form-group">
                  <label className="form-label">Tiempo de vida personalizado (min)</label>
                  <input
                    type="number"
                    className="form-input"
                    name="tiempo_vida_minutos"
                    min="0"
                    value={formValues.tiempo_vida_minutos}
                    onChange={handleChange}
                    required
                  />
                </div>
              ) : null}
            </>
          ) : null}
          {isWarmerProcess ? (
            <div className="form-group full-width">
              <label className="form-label">Tiempos por warmer (minutos)</label>
              {warmers?.length ? (
                <div className="form-grid">
                  {warmers.map((warmer) => (
                    <div className="form-group" key={warmer.id}>
                      <label className="form-label">{warmer.nombre}</label>
                      <input
                        type="number"
                        className="form-input"
                        min="0"
                        value={formValues.warmer_minutes?.[warmer.id] ?? ''}
                        onChange={(event) => handleWarmerMinutesChange(warmer.id, event.target.value)}
                        placeholder="Minutos en warmer"
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state-subtext">
                  No hay warmers activos. Configúralos en la sección Warmers.
                </div>
              )}
            </div>
          ) : null}
          <div className="form-group checkbox-group">
            <label className="checkbox-item">
              <input
                type="checkbox"
                name="activo"
                checked={formValues.activo}
                onChange={handleChange}
              />
              <span className="checkmark" />
              <span className="checkbox-label">Receta activa</span>
            </label>
          </div>
          <div className="form-group full-width">
            <label className="form-label">Imagen</label>
            <div className="image-upload-area" id="recipe-image-upload">
              {formValues.imagen_url ? (
                <div className="image-preview" id="recipe-image-preview">
                  <img src={formValues.imagen_url} alt={formValues.nombre || 'Receta'} className="image-preview-img" />
                  <button className="image-preview-remove" type="button" onClick={handleRemoveImage}>
                    <i data-lucide="x" />
                  </button>
                </div>
              ) : (
                <div className="image-placeholder">
                  <i data-lucide="image" />
                  <p>Arrastra una imagen o selecciónala</p>
                </div>
              )}
              <div className="image-actions">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  disabled={imageState.uploading}
                />
                {imageState.uploading ? <span className="uploading-text">Subiendo...</span> : null}
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn-secondary" type="button" onClick={onClose} disabled={state.saving}>
              Cancelar
            </button>
            <button className="btn-primary" type="submit" disabled={state.saving || imageState.uploading}>
              {state.saving ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RecipesSection;
